/**
 * The caller's plan id, for the surfaces that PACKAGE a feature rather than
 * bill for it.
 *
 * Shares `useCurrentSubscription`'s query key on purpose, so the billing page
 * and the transport ceiling are one cached read rather than two calls for the
 * same row.
 *
 * Every failure mode answers `null`, and `null` means "unrestricted" to every
 * consumer (see `transportCapabilitiesForPlan`). That direction is deliberate:
 * a subscriptions API that is slow, unreachable, or has no row for this customer
 * must not silently strip capabilities from a paying tenant — a packaging
 * decision the client cannot verify is one it should not enforce. The backend
 * gates the entitlements that actually matter.
 *
 * A subscription that is cancelled/expired resolves to the free tier rather than
 * to `null`: there the plan IS known, and it is the one the customer is on.
 */
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "@authe/ui-core/api";
import * as React from "react";
import {
  createApiSubscriptionsSource,
  isEntitledStatus,
  type SubscriptionSummary,
} from "@/api/subscriptionsSource";

/** The shared cache key for "the caller's current subscription". */
export const CURRENT_SUBSCRIPTION_QUERY_KEY = ["subscriptions", "current"] as const;

/** Plan id a subscription grants right now, or `null` when unknown. */
export function planIdFor(subscription: SubscriptionSummary | null | undefined): string | null {
  if (!subscription) return null;
  return isEntitledStatus(subscription.status) ? subscription.planId : "free";
}

export function useCurrentPlanId(): string | null {
  const client = useApiClient();
  const source = React.useMemo(() => createApiSubscriptionsSource(client), [client]);

  const query = useQuery({
    queryKey: CURRENT_SUBSCRIPTION_QUERY_KEY,
    queryFn: () => source.getCurrentSubscription(),
    // A plan does not change mid-session, and this read gates nothing the
    // backend is not already gating — so it is worth exactly one call.
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

  return planIdFor(query.data);
}
